/* eslint-disable @typescript-eslint/no-explicit-any */
import { reactive } from 'vue';
import axios from 'axios';
import qs from 'qs';

import { ExportedGlobalComposer, VueI18n } from 'vue-i18n';
import {
  LABEL_ACCOUNT_DISABLED,
  LABEL_PASSWORD_CANT_BE_RESET,
  LABEL_PASSWORD_RESET_DISABLED,
  LABEL_RECOVERY_EMAIL_MISMATCH,
  LABEL_SERVER_ERROR,
  LABEL_SERVER_ERROR_TRY_LATER,
} from '@/libs/i18nLabels';

let lastRequestLink: any = null;

export interface ResetKeyInfo {
  valid: boolean;
  errorCode: string | undefined;
}

export interface CaptachCallback {
  onCaptchaResolved(captcha: string): Promise<void>;
}

export interface PasswordServerInfo {
  supportEmail: string;
  serviceName: string;
  ui: Record<string, string>;
  disabled: boolean;
  captchaApiKey: string;
  captchaType: string;
  keyExpiration: number;
}

export interface PasswordResetLinkData {
  username: string;
  email?: string;
  captcha?: string;
}

export interface PasswordServerValidationError {
  field: string;
  code: string | undefined;
}

export enum PasswordResetErrorCode {
  InvalidKey = 'InvalidKey',
  ExpiredKey = 'ExpiredKey',
  InvalidPassword = 'InvalidPassword',
  TOTPInvalidCode = 'TOTPInvalidCode',
  TOTPRequired = 'TOTPRequired',
  TOTPInvalidBackupCode = 'TOTPInvalidBackupCode',
}

export interface PasswordResetResult {
  errorCode: PasswordResetErrorCode | undefined;
  signinMessage: string | undefined;
}

interface PasswordStateInterface {
  serverInfo: PasswordServerInfo | undefined;
  passwordResetResult: PasswordResetResult | undefined;
  serverLoading: boolean;
  lf: string;
  mobileApp: boolean;
}

export interface PasswordStateHandler {
  displayError(message: string): void;

  displayCaptcha(callback: CaptachCallback): void;
}

interface PasswordResetLinkResult {
  errorCode: string | undefined;
  errorMessage: string | undefined;
  notificationEmail: string | undefined;
  successMessage: string | undefined;
}

interface PasswordData {
  password: string;
  TOTPBackupCode?: string;
  TOTPCode?: string;
}

interface StoreInterface {
  state: PasswordStateInterface;
  handler: PasswordStateHandler | undefined;
  keyInfo: any;
  lfMailfence: boolean;

  isMailfence(): boolean;

  init(handler: PasswordStateHandler): void;
  fetchServerInfo(): Promise<PasswordServerInfo>;
  sendResetLink(data: PasswordResetLinkData, i18n: VueI18n | ExportedGlobalComposer): Promise<PasswordResetLinkResult | undefined>;

  resetPassword(data: PasswordData, i18n: VueI18n | ExportedGlobalComposer): Promise<PasswordResetResult | undefined>;

  displayError(err: string): void;
}

function getErrorMessage(res: any, i18n: VueI18n): string {
  if (res.status === 429) {
    return `Too Many Requests. ${i18n.t(LABEL_SERVER_ERROR_TRY_LATER)}`;
  }

  return `${res.status}: ${res.statusText || i18n.t(LABEL_SERVER_ERROR)}`;
}

function getCacheKey(data: PasswordResetLinkData) {
  return `${data.username}-${data.email}`.toLowerCase();
}

/**
 * Store
 */
const store: StoreInterface = {
  handler: undefined,
  lfMailfence: false,

  keyInfo: undefined,

  state: reactive({
    serverLoading: false,
    serverInfo: undefined,
    passwordResetResult: undefined,
    lf: '',
    mobileApp: false,
  }),

  init(handler: PasswordStateHandler) {
    this.handler = handler;
    const queryStringParams = qs.parse(window.location.search.substring(1)) as any;

    // eslint-disable-next-line
    const windowLF = (window as any)._lf;
    this.state.lf = queryStringParams.lf || windowLF || 'netbusiness';

    const source = queryStringParams.source || queryStringParams.src || undefined;
    const sourceLower = source && source.toLowerCase();
    this.state.mobileApp = sourceLower === 'ios_app' || sourceLower === 'android_app';

    this.lfMailfence = this.state.lf.toLowerCase().indexOf('mailfence') >= 0;
    if (this.lfMailfence) {
      document.body.classList.add('mailfence');
    }

    if (this.state.mobileApp) {
      document.body.classList.add('mobile-app');
    }

    // password key
    if (queryStringParams.k) {
      this.keyInfo = {};
      this.keyInfo.k = queryStringParams.k;
      this.keyInfo.a = queryStringParams.a;
      this.keyInfo.v = queryStringParams.v;
    } else {
      this.keyInfo = undefined;
    }
  },

  isMailfence() {
    return this.lfMailfence;
  },

  async fetchServerInfo(): Promise<PasswordServerInfo> {
    if (this.state.serverInfo) {
      return this.state.serverInfo;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const requestHeaders: any = {
      lf: this.state.lf,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = { ...this.keyInfo };

    const axiosResponse = await axios.get('/api/v1/password', {
      params,
      headers: requestHeaders,
    });

    const serverInfo = axiosResponse.data;
    this.state.serverInfo = serverInfo;
    this.state.passwordResetResult = serverInfo.resetKey as PasswordResetResult;

    if (!serverInfo.serviceName && serverInfo.ui) {
      serverInfo.serviceName = serverInfo.ui.ServiceName;
    }

    return this.state.serverInfo as PasswordServerInfo;
  },

  /**
   * Reset link post
   */
  async sendResetLink(data: PasswordResetLinkData, i18n: VueI18n): Promise<PasswordResetLinkResult | undefined> {
    const { locale } = i18n;

    // latest cached result to avoid to many server call
    if (lastRequestLink) {
      const key = getCacheKey(data);
      if (lastRequestLink.key === key) {
        return lastRequestLink.value as PasswordResetLinkResult;
      }
    }

    const requestHeaders: any = {
      lf: this.state.lf,
      locale,
    };

    try {
      const axiosResponse = await axios.post('/api/v1/password/reset', data, {
        headers: requestHeaders,

        validateStatus(status: number) {
          return status < 500; // Resolve only if the status code is less than 500
        },
      });

      const passwordResetData = axiosResponse.data as PasswordResetLinkResult;

      // captcha required handking
      if (axiosResponse.status === 400 && passwordResetData.errorCode === 'CaptchaRequired') {
        const onDone = new Promise<PasswordResetLinkResult | undefined>((resolve, reject) => {
          const callback = async (captcha: string) => {
            const dataWithToken: PasswordResetLinkData = { ...data, captcha };
            try {
              const result = await this.sendResetLink(dataWithToken, i18n);
              resolve(result);
            } catch (err) {
              reject(err);
            }
          };

          this.handler?.displayCaptcha({
            onCaptchaResolved: callback,
          });
        });

        return onDone;
      }

      if (axiosResponse.status === 200 || (axiosResponse.status === 400 && passwordResetData.errorCode)) {
        if (passwordResetData.errorCode === 'AccountAndEMailDoNotMatch') {
          passwordResetData.errorMessage = i18n.t(LABEL_RECOVERY_EMAIL_MISMATCH);
        } else if (passwordResetData.errorCode === 'AccountDisabled') {
          passwordResetData.errorMessage = `${i18n.t(LABEL_ACCOUNT_DISABLED)} ${i18n.t(LABEL_PASSWORD_CANT_BE_RESET)}`;
        } else if (passwordResetData.errorCode === 'PasswordResetDisabled') {
          passwordResetData.errorMessage = i18n.t(LABEL_PASSWORD_RESET_DISABLED, [this.state.serverInfo?.serviceName]);
        } else if (!passwordResetData.errorMessage) {
          passwordResetData.errorMessage = passwordResetData.errorCode;
        }

        // cache passwordResetData to avoid to many server call (only 400 errors)
        if (axiosResponse.status === 400) {
          lastRequestLink = { key: getCacheKey(data), value: passwordResetData };
        }

        return passwordResetData;
      }

      const errorMessage = getErrorMessage(axiosResponse, i18n);
      this.displayError(errorMessage);
      return undefined;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.log('Unable to query server:', err, err.response);
      let errorMessage = err.message;
      if (err.response) {
        errorMessage = getErrorMessage(err.response, i18n);
      }
      this.displayError(errorMessage);
      return Promise.resolve(undefined);
    }
  },

  async resetPassword(opt: PasswordData, i18n: VueI18n): Promise<PasswordResetResult | undefined> {
    const { locale } = i18n;

    const data: any = {
      ...this.keyInfo,
      password: opt.password,
      token: opt.TOTPCode,
      backupCode: opt.TOTPBackupCode,
    };

    const requestHeaders: any = {
      lf: this.state.lf,
      locale,
    };

    try {
      if (this.state.passwordResetResult) {
        // clear error code
        this.state.passwordResetResult.errorCode = undefined;
      }

      const axiosResponse = await axios.post('/api/v1/password', data, {
        headers: requestHeaders,

        validateStatus(status: number) {
          return status < 500; // Resolve only if the status code is less than 500
        },
      });

      const passwordResetData = axiosResponse.data as PasswordResetResult;

      if (axiosResponse.status === 200) {
        return passwordResetData;
      }

      if (axiosResponse.status === 400 && passwordResetData.errorCode && this.state.serverInfo) {
        this.state.passwordResetResult = passwordResetData;
        return passwordResetData;
      }

      const errorMessage = getErrorMessage(axiosResponse, i18n);
      this.displayError(errorMessage);
      return undefined;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.log('Unable to query server:', err, err.response);
      let errorMessage = err.message;
      if (err.response) {
        errorMessage = getErrorMessage(err.response, i18n);
      }
      this.displayError(errorMessage);
      return Promise.resolve(undefined);
    }
  },

  displayError(message: string) {
    this.handler?.displayError(message);
  },
};

export default store;
